import React from 'react';
import { Link } from 'react-router-dom';

// Internals
import { TODO } from '../../types';
import Typography from '../../components/Typography';
import TableCard from '../../components/TableCard';
import { truncate } from '../../utils';
import useProviders from '../../utils/useProviders';
import EmptyState from '../../components/EmptyState';

// Antd
import { Avatar, Button, Divider, message, PageHeader, Space, Spin } from 'antd';

// Icons
import { Plus, Search } from 'react-feather';

// Firebase
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../services/firebase';

// MISC
import { capitalize, get, uniqBy } from 'lodash-es';
import { formatDistance } from 'date-fns';

export default function Accounts() {
  const { getImageUsingID } = useProviders();

  const [user, loading, error] = useAuthState(auth);
  const [apiData, setApiData] = React.useState<Array<any> | null>(null);

  React.useEffect(() => {
    if (user && get(user, 'accessToken')) {
      fetch(process.env.REACT_APP_API_URL + '/accounts', {
        headers: new Headers({
          Authorization: 'Basic ' + (user as any).accessToken,
          'Content-Type': 'application/json',
        }),
      })
        .then((response) => response.json())
        .then(({ data }) => {
          setApiData(data);
        })
        .catch((e) => {
          console.log(e);

          message.error('Something went wrong');
        });
    }
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: TODO, record: any) => {
        const PROVIDER_IMAGE = getImageUsingID(record.provider);

        return (
          <Space>
            <Avatar src={PROVIDER_IMAGE} size="large" />

            <div>
              <p>{text}</p>

              <Typography type="text_sm" color="secondary">
                {truncate(record.address)}
              </Typography>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <>{capitalize(type)}</>,
    },
    {
      title: 'Transactions',
      dataIndex: 'txnCount',
      key: 'txnCount',
    },
    {
      title: 'Available balance',
      key: 'balances',
      dataIndex: 'balances',
      render: (balances: TODO, record: TODO) => {
        let balance = null;
        if (balances && balances.length !== 0) {
          balance = balances.reduce(
            (accum: number, balance: any) => accum + parseFloat(balance.price),
            0,
          );
        }
        return balance && <>${balance.toFixed(2)} USD</>;
      },
    },
    {
      title: 'Last refresh',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      render: (text: TODO, record: TODO) => <>{formatDistance(new Date(text), new Date())} ago</>,
    },
    {
      title: 'Currencies',
      key: 'balances',
      dataIndex: 'balances',
      render: (text: TODO, record: TODO) => {
        return (
          <>
            <Avatar.Group style={{ verticalAlign: 'middle' }}>
              {uniqBy(
                record.balances.filter((balance: any) => balance.icon),
                'icon',
              ).map((balance: any) => (
                <Avatar style={{ backgroundColor: '#fff' }} size={32} src={balance.icon} />
              ))}
            </Avatar.Group>
          </>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        ghost={false}
        title={
          <>
            <Typography type="display_sm" weight="medium">
              Accounts
            </Typography>

            <Typography type="text_md" color="secondary">
              Add and manage all of your accounts
            </Typography>
          </>
        }
        extra={[
          <Link to="/accounts/add">
            <Button key="2" type="primary" size="large">
              <Plus /> Add account
            </Button>
          </Link>,
        ]}
      />

      <Divider />

      <Spin spinning={apiData === null}>
        {Array.isArray(apiData) && apiData.length !== 0 && (
          <TableCard
            checkboxSelection={false}
            dataSource={apiData}
            columns={columns}
            showCardActions={false}
            tableRowKey="id"
            title={'Connected Wallets'}
          />
        )}

        {Array.isArray(apiData) && apiData.length === 0 && (
          <EmptyState
            title="No account have been added yet"
            subTitle="Connect wallets, custodians, exchanges, and accounting software to see the magic happen!"
            action={
              <Link to="/accounts/add">
                <Button type="primary" size="large">
                  <Plus /> Add account
                </Button>
              </Link>
            }
            icon={<Search />}
          />
        )}
      </Spin>
    </div>
  );
}
